/**
 * Contract Edit Tab — עריכת חוזה (ניהול, admin-only)
 *
 * Loads the current signup agreement (הסכם ייעוץ השקעות) and lets an admin edit
 * its text in a WYSIWYG editor. Saving publishes a NEW version that applies to
 * NEW signers only — users who already signed keep the exact version they signed.
 */

import { useEffect, useRef, useState, useCallback } from 'react'
import api from '../../services/api'
import {
  Bold, Italic, List, ListOrdered, Heading2, Undo2, Redo2,
  Save, RefreshCw, History, Check, AlertCircle,
} from 'lucide-react'

interface ContractData {
  version: number
  title: string
  bodyHtml: string
  updatedAt?: string
}

interface ContractVersionMeta {
  version: number
  title: string
  isCurrent: boolean
  createdAt: string
}

function formatDate(iso?: string): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleString('he-IL', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  })
}

// Styling shared by the editor + preview so the admin sees the contract roughly
// as it renders in the signed PDF.
const CONTRACT_STYLE = `
  .contract-doc { direction: rtl; font-size: 14px; line-height: 1.7; color: #1a1a1a; }
  .contract-doc h2 { font-size: 16px; font-weight: 700; margin: 18px 0 8px; }
  .contract-doc .section { margin-bottom: 14px; }
  .contract-doc .section h2 { font-size: 15px; margin-bottom: 6px; }
  .contract-doc .clause { margin-bottom: 6px; }
  .contract-doc .preamble p { margin-bottom: 4px; }
  .contract-doc strong { font-weight: 700; }
  .contract-doc ul, .contract-doc ol { padding-inline-start: 22px; margin: 6px 0; }
  .contract-doc .def-table { width: 100%; border-collapse: collapse; margin: 8px 0 14px; font-size: 13px; }
  .contract-doc .def-table td { vertical-align: top; padding: 4px 8px; border-bottom: 1px solid #eee; }
  .contract-doc .def-table td:first-child { font-weight: 700; width: 150px; white-space: nowrap; }
`

export default function ContractEditTab() {
  const editorRef = useRef<HTMLDivElement>(null)

  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  const [version, setVersion] = useState<number | null>(null)
  const [updatedAt, setUpdatedAt] = useState<string | undefined>()
  const [title, setTitle] = useState('')

  // Originals to detect unsaved changes
  const [originalTitle, setOriginalTitle] = useState('')
  const [originalBody, setOriginalBody] = useState('')
  const [bodyHtml, setBodyHtml] = useState('')

  const [history, setHistory] = useState<ContractVersionMeta[]>([])
  const [showHistory, setShowHistory] = useState(false)

  // The editable <div> only mounts after loading finishes, so we seed its
  // innerHTML in an effect (once) rather than inside load(), where the ref is null.
  const seededRef = useRef(false)

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    seededRef.current = false
    try {
      const res = await api.get<ContractData>('/admin/contract')
      const data = res.data
      setVersion(data.version)
      setUpdatedAt(data.updatedAt)
      setTitle(data.title)
      setOriginalTitle(data.title)
      setOriginalBody(data.bodyHtml)
      setBodyHtml(data.bodyHtml)
    } catch (err: any) {
      setError(err.response?.data?.message || 'שגיאה בטעינת החוזה')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  // Seed the editor with the loaded contract once it is mounted (loading === false).
  useEffect(() => {
    if (!loading && !seededRef.current && editorRef.current) {
      editorRef.current.innerHTML = originalBody
      seededRef.current = true
    }
  }, [loading, originalBody])

  const loadHistory = useCallback(async () => {
    try {
      const res = await api.get<{ versions: ContractVersionMeta[] }>('/admin/contract/history')
      setHistory(res.data.versions)
    } catch {
      /* non-critical */
    }
  }, [])

  const dirty = title !== originalTitle || bodyHtml !== originalBody

  const syncFromEditor = () => {
    if (editorRef.current) setBodyHtml(editorRef.current.innerHTML)
    setSuccess('')
  }

  // execCommand keeps the toolbar simple + dependency-free. We preventDefault on
  // mousedown so the editor keeps its selection when a button is clicked.
  const exec = (command: string, value?: string) => {
    editorRef.current?.focus()
    document.execCommand(command, false, value)
    syncFromEditor()
  }

  const handleSave = async () => {
    const currentBody = editorRef.current?.innerHTML || bodyHtml
    if (!title.trim()) {
      setError('כותרת ההסכם חסרה')
      return
    }
    if (currentBody.replace(/<[^>]*>/g, '').trim().length < 20) {
      setError('תוכן ההסכם קצר מדי')
      return
    }
    const ok = window.confirm(
      'פרסום גרסה חדשה של החוזה?\n\nהגרסה החדשה תחול על לקוחות שיחתמו מרגע זה ואילך בלבד. ' +
      'לקוחות שכבר חתמו ימשיכו להחזיק בגרסה שעליה חתמו.'
    )
    if (!ok) return

    setSaving(true)
    setError('')
    setSuccess('')
    try {
      const res = await api.put<ContractData & { message: string }>('/admin/contract', {
        title: title.trim(),
        bodyHtml: currentBody,
      })
      const data = res.data
      // Server sanitizes → re-sync editor with the stored HTML
      setVersion(data.version)
      setUpdatedAt(data.updatedAt)
      setTitle(data.title)
      setOriginalTitle(data.title)
      setOriginalBody(data.bodyHtml)
      setBodyHtml(data.bodyHtml)
      if (editorRef.current) editorRef.current.innerHTML = data.bodyHtml
      setSuccess(data.message || 'ההסכם עודכן בהצלחה')
      if (showHistory) loadHistory()
    } catch (err: any) {
      setError(err.response?.data?.error || err.response?.data?.message || 'שגיאה בעדכון החוזה')
    } finally {
      setSaving(false)
    }
  }

  const handleReset = () => {
    setTitle(originalTitle)
    setBodyHtml(originalBody)
    if (editorRef.current) editorRef.current.innerHTML = originalBody
    setError('')
    setSuccess('')
  }

  const toggleHistory = () => {
    const next = !showHistory
    setShowHistory(next)
    if (next && history.length === 0) loadHistory()
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20 text-gray-500">
        <RefreshCw className="animate-spin ml-2" size={20} /> טוען את החוזה…
      </div>
    )
  }

  return (
    <div dir="rtl" className="max-w-4xl">
      <style>{CONTRACT_STYLE}</style>

      {/* Header */}
      <div className="mb-4">
        <h2 className="text-xl font-bold text-gray-900 mb-1">עריכת חוזה</h2>
        <p className="text-gray-600 text-sm">
          עריכת נוסח הסכם ייעוץ ההשקעות שנחתם על ידי לקוחות חדשים. שמירה יוצרת גרסה חדשה
          שתחול על לקוחות שיחתמו מרגע זה ואילך בלבד — לקוחות קיימים לא יושפעו.
        </p>
        <p className="text-gray-500 text-xs mt-1">
          גרסה נוכחית: <strong>{version}</strong>
          {updatedAt && <> · עודכן לאחרונה: {formatDate(updatedAt)}</>}
        </p>
      </div>

      {/* Alerts */}
      {error && (
        <div className="mb-4 flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-2 text-sm">
          <AlertCircle size={16} /> {error}
        </div>
      )}
      {success && (
        <div className="mb-4 flex items-center gap-2 bg-green-50 border border-green-200 text-green-700 rounded-lg px-4 py-2 text-sm">
          <Check size={16} /> {success}
        </div>
      )}

      {/* Title */}
      <label className="block text-sm font-medium text-gray-700 mb-1">כותרת ההסכם</label>
      <input
        type="text"
        value={title}
        onChange={(e) => { setTitle(e.target.value); setSuccess('') }}
        className="w-full border border-gray-300 rounded-lg px-3 py-2 mb-4 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
        placeholder="הסכם ייעוץ השקעות"
      />

      {/* Toolbar */}
      <label className="block text-sm font-medium text-gray-700 mb-1">תוכן ההסכם</label>
      <div className="flex flex-wrap gap-1 border border-gray-300 border-b-0 rounded-t-lg bg-gray-50 px-2 py-1">
        <ToolbarButton title="מודגש" onClick={() => exec('bold')}><Bold size={16} /></ToolbarButton>
        <ToolbarButton title="נטוי" onClick={() => exec('italic')}><Italic size={16} /></ToolbarButton>
        <ToolbarButton title="כותרת סעיף" onClick={() => exec('formatBlock', 'h2')}><Heading2 size={16} /></ToolbarButton>
        <ToolbarButton title="פסקה רגילה" onClick={() => exec('formatBlock', 'p')}><span className="text-xs font-semibold px-1">טקסט</span></ToolbarButton>
        <ToolbarButton title="רשימת תבליטים" onClick={() => exec('insertUnorderedList')}><List size={16} /></ToolbarButton>
        <ToolbarButton title="רשימה ממוספרת" onClick={() => exec('insertOrderedList')}><ListOrdered size={16} /></ToolbarButton>
        <div className="w-px bg-gray-300 mx-1" />
        <ToolbarButton title="בטל" onClick={() => exec('undo')}><Undo2 size={16} /></ToolbarButton>
        <ToolbarButton title="בצע שוב" onClick={() => exec('redo')}><Redo2 size={16} /></ToolbarButton>
      </div>

      {/* Editable area */}
      <div
        ref={editorRef}
        role="textbox"
        aria-multiline="true"
        aria-label="תוכן ההסכם"
        contentEditable
        suppressContentEditableWarning
        onInput={syncFromEditor}
        className="contract-doc border border-gray-300 rounded-b-lg bg-white px-5 py-4 min-h-[400px] max-h-[600px] overflow-y-auto focus:outline-none focus:ring-2 focus:ring-primary-500"
      />

      {/* Actions */}
      <div className="flex items-center gap-3 mt-4">
        <button
          onClick={handleSave}
          disabled={saving || !dirty}
          className="flex items-center gap-2 bg-primary-600 text-white rounded-lg px-4 py-2 text-sm font-medium hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saving ? <RefreshCw className="animate-spin" size={16} /> : <Save size={16} />}
          פרסום גרסה חדשה
        </button>
        <button
          onClick={handleReset}
          disabled={saving || !dirty}
          className="text-sm text-gray-600 hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          ביטול שינויים
        </button>
        <button
          onClick={toggleHistory}
          className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900 mr-auto"
        >
          <History size={16} /> היסטוריית גרסאות
        </button>
      </div>

      {dirty && (
        <p className="text-xs text-amber-600 mt-2">יש שינויים שלא נשמרו.</p>
      )}

      {/* Version history */}
      {showHistory && (
        <div className="mt-6 border border-gray-200 rounded-lg overflow-hidden">
          <div className="bg-gray-50 px-4 py-2 text-sm font-medium text-gray-700 border-b border-gray-200">
            היסטוריית גרסאות
          </div>
          {history.length === 0 ? (
            <div className="px-4 py-3 text-sm text-gray-500">אין גרסאות להצגה.</div>
          ) : (
            <ul className="divide-y divide-gray-100">
              {history.map((v) => (
                <li key={v.version} className="px-4 py-2 text-sm flex items-center gap-3">
                  <span className="font-semibold text-gray-800">גרסה {v.version}</span>
                  {v.isCurrent && (
                    <span className="text-xs bg-green-100 text-green-700 rounded px-2 py-0.5">נוכחית</span>
                  )}
                  <span className="text-gray-500 truncate">{v.title}</span>
                  <span className="text-gray-400 text-xs mr-auto">{formatDate(v.createdAt)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  )
}

function ToolbarButton({
  title, onClick, children,
}: {
  title: string
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      // Keep the editor selection when clicking a toolbar button
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      className="flex items-center justify-center h-8 min-w-8 px-1.5 rounded text-gray-700 hover:bg-gray-200"
    >
      {children}
    </button>
  )
}
